import React from "react";
import { ChevronDown, ChevronRight, Video, FileText, Image as ImageIcon, ExternalLink, User, Calendar, Tag } from "lucide-react";
import { formatDateIT } from "@/foundation/formatting";
import { GuideFeedback } from "./GuideFeedback";

const MEDIA_ICONS = {
  video: Video,
  pdf: FileText,
  image: ImageIcon,
  external: ExternalLink,
};

function MediaSlot({ media }) {
  const Icon = MEDIA_ICONS[media.kind] || FileText;
  const isPlaceholder = media.placeholder || !media.url;
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] ${
        isPlaceholder
          ? "border-dashed border-suite-neutral-300 bg-suite-neutral-50 text-suite-neutral-400"
          : "border-suite-primary/30 bg-suite-primary/5 text-suite-primary hover:bg-suite-primary/10"
      }`}
    >
      <Icon size={12} />
      <span className="font-medium">{media.title || media.kind}</span>
      {isPlaceholder && (
        <span className="italic">(in preparazione)</span>
      )}
    </div>
  );
}

/**
 * Contenuto centrale del Centro Guida.
 * Renderizza capitoli espandibili + articoli con body, media, keywords.
 *
 * Props:
 *  - appKey                (per il feedback Foundation)
 *  - chapters
 *  - expanded, onToggleChapter
 *  - contentRef (ref) - per lo scroll target
 *  - feedbackSummaries?    Map articleId -> {helpful, not_helpful, my_verdict, ...}
 */
export function GuideContent({
  appKey,
  chapters,
  expanded,
  onToggleChapter,
  contentRef,
  feedbackSummaries = {},
  packVersion = "",
}) {
  return (
    <main
      ref={contentRef}
      className="min-w-0"
      data-testid="guide-content"
    >
      {chapters.length === 0 && (
        <div className="rounded-lg border border-dashed border-suite-neutral-300 bg-suite-neutral-50 p-8 text-center text-sm text-suite-neutral-500">
          Non ci sono contenuti disponibili per il tuo ruolo in questo Pack.
        </div>
      )}
      {chapters.map((ch, i) => (
        <section
          key={ch.id}
          id={`guide-ch-${ch.id}`}
          className="mb-3 rounded-lg border border-suite-neutral-200 bg-white"
          data-testid={`guide-chapter-${ch.id}`}
        >
          <button
            type="button"
            onClick={() => onToggleChapter?.(ch.id)}
            className="flex w-full items-center gap-2 border-b border-suite-neutral-100 px-4 py-3 text-left hover:bg-suite-neutral-50"
            data-testid={`guide-chapter-toggle-${ch.id}`}
          >
            {expanded[ch.id] ? (
              <ChevronDown size={16} className="text-suite-primary" />
            ) : (
              <ChevronRight size={16} className="text-suite-neutral-400" />
            )}
            <span className="text-sm font-semibold text-suite-neutral-900">
              <span className="mr-1 text-suite-neutral-400">{i + 1}.</span>
              {ch.title}
            </span>
          </button>
          {expanded[ch.id] && (
            <div className="space-y-3 px-4 py-4">
              {ch.description && (
                <p className="text-xs italic text-suite-neutral-500">
                  {ch.description}
                </p>
              )}
              {ch.articles.map((a, j) => (
                <article
                  key={a.id}
                  id={`guide-art-${a.id}`}
                  className="rounded-md border border-suite-neutral-100 bg-white p-3 transition-shadow"
                  data-testid={`guide-article-${a.id}`}
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-suite-primary">
                      {i + 1}.{j + 1} · {a.title}
                    </div>
                    {a.category && (
                      <span className="text-[10px] uppercase tracking-[0.1em] text-suite-neutral-400">
                        {a.category}
                      </span>
                    )}
                  </div>
                  {a.description && (
                    <p className="mb-2 text-xs text-suite-neutral-500">
                      {a.description}
                    </p>
                  )}
                  <div className="space-y-1.5 text-sm leading-relaxed text-suite-neutral-800">
                    {a.body.map((line, k) => (
                      <p key={k}>{line}</p>
                    ))}
                  </div>
                  {a.media?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {a.media.map((m, k) => (
                        <MediaSlot key={k} media={m} />
                      ))}
                    </div>
                  )}
                  {a.keywords?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {a.keywords.slice(0, 6).map((k) => (
                        <span
                          key={k}
                          className="rounded-full bg-suite-neutral-100 px-2 py-0.5 text-[10px] text-suite-neutral-500"
                        >
                          #{k}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Meta articolo Foundation: autore, data, versione */}
                  {(a.updated_at || a.author || a.min_version || packVersion) && (
                    <div
                      className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-suite-neutral-100 pt-2 text-[10px] text-suite-neutral-500"
                      data-testid={`guide-article-meta-${a.id}`}
                    >
                      {a.updated_at && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={10} />
                          Aggiornato il <b className="text-suite-neutral-700">{formatDateIT(a.updated_at)}</b>
                        </span>
                      )}
                      {(a.min_version || packVersion) && (
                        <span className="inline-flex items-center gap-1">
                          <Tag size={10} />
                          Versione <b className="text-suite-neutral-700">{a.min_version || packVersion}</b>
                        </span>
                      )}
                      {a.author && (
                        <span className="inline-flex items-center gap-1">
                          <User size={10} />
                          Autore <b className="text-suite-neutral-700">{a.author}</b>
                        </span>
                      )}
                    </div>
                  )}
                  {/* Feedback utente Foundation */}
                  {appKey && (
                    <GuideFeedback
                      appKey={appKey}
                      articleId={a.id}
                      initialSummary={feedbackSummaries[a.id]}
                    />
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      ))}
    </main>
  );
}
